import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  merge,
  Observable,
  of,
  Subject,
  switchMap
} from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { EnterpriseBasketService } from '@sabstravtech/obtservices/angular';
import { ChatMiAnswer, ChatMiField, ChatMiValue } from '@sabstravtech/obtservices/base';

/**
 * ENT-16431 — renders a single MI (cost centre) question inside the chat widget.
 * Supports the three input methods (drop-down, type-ahead, free text) driven by
 * the field's `uiPresentation`, prepopulates any preselected editable value, and
 * gates the Continue button on validity. Continue is always active for optional
 * fields. Emits the collected {@link ChatMiAnswer} on confirm.
 */
@Component({
  selector: 'app-chat-mi-input',
  standalone: false,
  templateUrl: './chat-mi-input.component.html',
  styleUrls: ['./chat-mi-input.component.scss']
})
export class ChatMiInputComponent implements OnChanges {
  @Input() field!: ChatMiField;
  @Input() selectedValue?: ChatMiAnswer | null;
  @Input() required?: boolean;
  @Input() error?: string;
  @Output() answer = new EventEmitter<ChatMiAnswer>();

  @ViewChild('autosuggestInput') autosuggestInput?: NgbTypeahead;

  /** Free-text value or the chosen value's id (dropdown). */
  textValue = '';
  /** The selected dropdown/auto-suggest value object. */
  selectedMiValue: ChatMiValue | null = null;
  autosuggestClick$ = new Subject<string>();
  isLoading = new BehaviorSubject<boolean>(false);

  constructor(private basketService: EnterpriseBasketService) {}

  ngOnChanges(): void {
    // Prepopulate any preselected editable value.
    const preselected = this.selectedValue;
    if (preselected?.managementInfoValue) {
      this.selectedMiValue = preselected.managementInfoValue;
      this.textValue = preselected.managementInfoValue.value;
    } else if (preselected?.freeValue) {
      this.textValue = preselected.freeValue;
    }
  }

  get isMandatory(): boolean {
    return this.field.mandatory;
  }

  get isDropdown(): boolean {
    return this.field.uiPresentation === 'DROPDOWN';
  }

  get isAutoSuggest(): boolean {
    return this.field.uiPresentation === 'AUTO_SUGGEST' || this.field.uiPresentation === 'LOOKUP';
  }

  get isFreeText(): boolean {
    return this.field.uiPresentation === 'FREE_TEXT';
  }

  onDropdownChange(valueId: string): void {
    this.selectedMiValue = this.field.availableValues?.find((v) => v.id === valueId) ?? null;
  }

  search = (terms$: Observable<string>): Observable<ChatMiValue[]> => {
    const debounced$ = terms$.pipe(debounceTime(300), distinctUntilChanged());
    const clicks$ = this.autosuggestClick$.pipe(
      filter(() => !!this.autosuggestInput && !this.autosuggestInput.isPopupOpen()),
      map(() => '')
    );
    return merge(debounced$, clicks$).pipe(
      switchMap((term) => {
        if (!term) {
          return of<ChatMiValue[]>([]);
        }
        return this.basketService
          .getMiAutoSugestions(this.field.id, term, this.isLoading)
          .pipe(map((values) => values as unknown as ChatMiValue[]));
      })
    );
  };

  formatValue(item: ChatMiValue | string): string {
    const value = item as ChatMiValue;
    if (value?.value && value?.label) {
      return `${value.value} (${value.label})`;
    }
    return value?.value ?? (item as string);
  }

  onAutoSuggestSelect(value: ChatMiValue): void {
    this.selectedMiValue = value;
  }

  private freeTextValid(): boolean {
    const value = this.textValue?.trim() ?? '';
    if (!value) {
      return !this.isMandatory;
    }
    if (this.field.valueMask) {
      try {
        return new RegExp(this.field.valueMask).test(value);
      } catch {
        return true;
      }
    }
    return true;
  }

  /**
   * Show a format hint under free text when the user has typed something that
   * fails the configured regex (`valueMask`), guiding them before they submit.
   */
  get showFormatHint(): boolean {
    return (
      this.isFreeText && !!this.field.valueMask && !!this.textValue?.trim() && !this.freeTextValid()
    );
  }

  get isValid(): boolean {
    if (this.isFreeText) {
      return this.freeTextValid();
    }
    if (this.isDropdown || this.isAutoSuggest) {
      if (this.selectedMiValue) {
        return true;
      }
      return !this.isMandatory;
    }
    return true;
  }

  onContinue(): void {
    if (!this.isValid) {
      return;
    }
    if (this.isFreeText) {
      this.answer.emit({
        selectionId: this.selectedValue?.selectionId ?? null,
        freeValue: this.textValue?.trim() ?? ''
      });
      return;
    }
    this.answer.emit({
      selectionId: this.selectedValue?.selectionId ?? null,
      managementInfoValue: this.selectedMiValue ?? undefined
    });
  }

  /**
   * Optional-field-only "Skip" button — discards whatever's currently typed
   * or selected and moves on with an empty answer, same as pressing Continue
   * with nothing entered.
   */
  onSkip(): void {
    if (this.isMandatory) {
      return;
    }
    this.textValue = '';
    this.selectedMiValue = null;
    if (this.isFreeText) {
      this.answer.emit({
        selectionId: this.selectedValue?.selectionId ?? null,
        freeValue: ''
      });
      return;
    }
    this.answer.emit({
      selectionId: this.selectedValue?.selectionId ?? null,
      managementInfoValue: undefined
    });
  }
}
